import './StaticMultiSelectControl.scss';

import React, { useCallback, useMemo } from 'react';

import type { FilterConfig, FilterOperator, FilterOption } from '../../../../../apis/graphql/types';
import { FilterOptionSource } from '../../../../../apis/graphql/types';
import type { DropdownOption } from '../../../../../common/Dropdown';
import Dropdown from '../../../../../common/Dropdown';
import { commonCustomComponents } from '../../../../../common/Dropdown/Dropdown';
import MultiSelectDropdown from '../../../../../common/MultiSelectDropdown';
import { getNoOptionsMessage } from '../../../../../common/TargetingDropdowns/utils';
import { useGetFilterOptions } from '../../../TableFilterBar/AdditionalFilters/hooks/useGetFilterOptions';
import MenuList from '../../../TableFilterBar/MenuList';
import useScrollAwareMenuPlacement from '../../hooks/useScrollAwareMenuPlacement';
import type { MultiSelectState } from '../FilterRow';
import { useMultiSelectHandlers } from '../hooks/useMultiSelectHandlers';

const OPERATOR_LABELS: Partial<Record<string, string>> = {
  IN: 'Is',
  NOT_IN: 'Is Not',
  CONTAINS: 'Contains',
  DOES_NOT_CONTAIN: 'Does Not Contain',
};

export type StaticMultiSelectControlProps = {
  config: FilterConfig;
  value: MultiSelectState;
  onChange: (id: string, value: MultiSelectState) => void;
};

type MultiSelectCheckboxProps = StaticMultiSelectControlProps & {
  options: FilterOption[];
  hasMore?: boolean;
  error?: string;
  onNext?: () => Promise<void>;
};

// Presentational, non-searchable checkbox dropdown shared by the static and
// dynamic (non-searchable) option sources. It is controlled: the selected value
// lives in the parent FilterPanel draft and is echoed back through `onChange`.
const MultiSelectCheckbox = ({
  config,
  value,
  onChange,
  options,
  hasMore = false,
  error,
  onNext,
}: MultiSelectCheckboxProps): JSX.Element => {
  const { menuPlacement, maxMenuHeight, placementRef, onOpenMenu } = useScrollAwareMenuPlacement({
    hasSearchBar: false,
  });
  const components = useMemo(() => ({ ...commonCustomComponents, MenuList }), []);

  const handleOptionsChange = useCallback(
    (id: string, newOptions: FilterOption[]): void => {
      onChange(id, { operator: value.operator, options: newOptions });
    },
    [onChange, value.operator]
  );

  const { selectedDropdownOptions, handleChange, handleSelectAll, handleClearAll } = useMultiSelectHandlers({
    id: config.id,
    value: value.options,
    options,
    onChange: handleOptionsChange,
  });

  const operatorOptions: DropdownOption[] = (config.operators ?? []).map((op) => ({
    label: OPERATOR_LABELS[op] ?? op,
    value: op,
  }));

  const selectedOperatorOption = operatorOptions.find((o) => o.value === value.operator) ?? null;

  const handleOperatorChange = useCallback(
    (option: DropdownOption | null): void => {
      if (!option) return;
      onChange(config.id, { operator: option.value as FilterOperator, options: value.options });
    },
    [config.id, onChange, value.options]
  );

  const showOperatorToggle = !!config.includeExclude?.excludeKey;

  // Non-searchable: there is no search input, so always report options as
  // available (supportEmptySearch = true) rather than "start typing".
  const noOptionsMessage = useMemo(() => (): string => getNoOptionsMessage(false, true, ''), []);

  return (
    <div
      className="static-multi-select-control"
      data-testid="static-multi-select-control"
      ref={placementRef}
      onMouseDown={onOpenMenu}
    >
      {showOperatorToggle && (
        <div className="static-multi-select-control__operator-dropdown">
          <Dropdown
            options={operatorOptions}
            value={selectedOperatorOption}
            onChange={handleOperatorChange}
            dataTestId="multi-select-operator-dropdown"
            isSearchable={false}
            menuPlacement={menuPlacement}
            maxMenuHeight={maxMenuHeight}
          />
        </div>
      )}
      <MultiSelectDropdown<string>
        value={selectedDropdownOptions}
        options={options}
        components={components}
        multipleSelectControlsInMenu
        isSearchable={false}
        hasMore={hasMore}
        error={error}
        onNext={onNext}
        onChange={handleChange}
        handleSelectAll={handleSelectAll}
        handleClearAll={handleClearAll}
        noOptionsMessage={noOptionsMessage}
        emptyDisplayText={`Select ${config.label}...`}
        displayMode="count"
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
      />
    </div>
  );
};

// DYNAMIC, non-searchable source: options come from getFilterOptions in a single
// one-shot fetch (hasNextPage is always false for these enum-backed fields).
const DynamicOptionsCheckbox = ({ config, value, onChange }: StaticMultiSelectControlProps): JSX.Element => {
  const { options, hasMore, error, onNext } = useGetFilterOptions(config.id, false);

  return (
    <MultiSelectCheckbox
      config={config}
      value={value}
      onChange={onChange}
      options={options}
      hasMore={hasMore}
      error={error}
      onNext={onNext}
    />
  );
};

// Catch-all multi-select for non-boolean, non-searchable, non-Calendar,
// non-Range filters (CHECKBOX/CHECKBOXES/DROPDOWN/RADIOBUTTON enum lists).
// Branches on the option source so the dynamic fetch hook is only ever mounted
// for DYNAMIC configs, keeping the Rules of Hooks satisfied for static configs.
const StaticMultiSelectControl = ({ config, value, onChange }: StaticMultiSelectControlProps): JSX.Element => {
  if (config.optionSource === FilterOptionSource.Dynamic) {
    return <DynamicOptionsCheckbox config={config} value={value} onChange={onChange} />;
  }

  const staticOptions: FilterOption[] = (config.staticOptions ?? []).map((o) => ({ value: o.value, label: o.label }));

  return <MultiSelectCheckbox config={config} value={value} onChange={onChange} options={staticOptions} />;
};

export default StaticMultiSelectControl;
